import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { notification } from 'antd';
import {
	addMoreConversations,
	addMoreMessages,
	setLoadingMore,
} from '@/store/textingPlatform/slice';
import { PaginatedResponse } from '@/types/TTextingPlatform';
import { getTextingConversations } from '@/api/textingPlatform';
import { selectPaginationState } from '@/store/textingPlatform/selector';

export const useTextingPagination = (selectedAgentId: string, provisionedNumber: string) => {
	const dispatch = useDispatch();
	const paginationState = useSelector(selectPaginationState);

	const { conversationCursor, hasMoreConversations, isLoadingMore } = paginationState || {
		conversationCursor: null,
		hasMoreConversations: false,
		isLoadingMore: false,
	};

	const loadMoreConversations = useCallback(async () => {
		try {
			if (
				!hasMoreConversations ||
				isLoadingMore ||
				!selectedAgentId ||
				!provisionedNumber ||
				!conversationCursor
			) {
				return;
			}

			dispatch(setLoadingMore(true));

			const result = (await getTextingConversations(
				selectedAgentId,
				provisionedNumber,
				'more_conversations',
				conversationCursor,
			)) as PaginatedResponse;

			if (result?.conversations) {
				dispatch(
					addMoreConversations({
						conversations: result.conversations,
						nextCursor: result.nextCursor,
						hasMore: result.hasMore,
					}),
				);
			} else {
				dispatch(
					addMoreConversations({
						conversations: {},
						nextCursor: null,
						hasMore: false,
					}),
				);
			}
		} catch (_error) {
			notification.error({
				message: 'Error',
				description: 'Failed to load more conversations',
				duration: 3,
			});
			dispatch(
				addMoreConversations({
					conversations: {},
					nextCursor: null,
					hasMore: false,
				}),
			);
		} finally {
			dispatch(setLoadingMore(false));
		}
	}, [
		dispatch,
		selectedAgentId,
		provisionedNumber,
		conversationCursor,
		hasMoreConversations,
		isLoadingMore,
	]);

	const loadMoreMessages = useCallback(
		async (phoneNumber: string, messageCursor: string) => {
			try {
				if (!selectedAgentId || !provisionedNumber || !phoneNumber || !messageCursor) {
					return [];
				}

				const cursor = JSON.stringify({ phoneNumber, timestamp: messageCursor });
				const result = (await getTextingConversations(
					selectedAgentId,
					provisionedNumber,
					'more_messages',
					cursor,
				)) as PaginatedResponse;

				if (result?.messages && Array.isArray(result.messages)) {
					dispatch(
						addMoreMessages({
							phoneNumber,
							messages: result.messages,
							hasMore: result.hasMore,
						}),
					);

					return result.messages;
				}

				return [];
			} catch (_error) {
				notification.error({
					message: 'Error',
					description: 'Failed to load more messages',
					duration: 3,
				});
				return [];
			}
		},
		[dispatch, selectedAgentId, provisionedNumber],
	);

	return {
		loadMoreConversations,
		loadMoreMessages,
		hasMoreConversations: Boolean(hasMoreConversations),
		isLoadingMore: Boolean(isLoadingMore),
	};
};
